import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useActingOrg } from '../context/ActingOrgContext';
import { schoolService } from '../services/schoolService';
import { questionnaireService } from '../services/questionnaireService';
import { dashboardService } from '../services/dashboardService';
import { orgManagementService, Organization } from '../services/orgManagementService';
import OnboardingGuide from '../components/OnboardingGuide';
import { useOnboardingGuide } from '../hooks/useOnboardingGuide';
import { Questionnaire } from '../types';

interface DashboardStats {
  schoolCount: number;
  questionnaireCount: number;
  /** 今日新增答卷数（completed_at 落今天的 COMPLETED user_questionnaire 数） */
  todayCompletedAnswers: number;
  publishedCount: number;
}

const DashboardPage: React.FC = () => {
  const { user, isSuperAdmin } = useAuth();
  const { actingOrg } = useActingOrg();
  const navigate = useNavigate();
  const [orgs, setOrgs] = useState<Organization[]>([]);
  const [selectedOrgId, setSelectedOrgId] = useState<number | undefined>(undefined);
  const showOrgFilter = isSuperAdmin && actingOrg == null;
  const [stats, setStats] = useState<DashboardStats>({
    schoolCount: 0,
    questionnaireCount: 0,
    todayCompletedAnswers: 0,
    publishedCount: 0,
  });
  const [recentQuestionnaires, setRecentQuestionnaires] = useState<Questionnaire[]>([]);
  const [allQuestionnaires, setAllQuestionnaires] = useState<Questionnaire[]>([]);
  const [loading, setLoading] = useState(true);

  const onboarding = useOnboardingGuide({
    schoolCount: stats.schoolCount,
    questionnaireCount: stats.questionnaireCount,
    publishedCount: stats.publishedCount,
    questionnaires: allQuestionnaires,
  });

  useEffect(() => {
    fetchDashboardData(selectedOrgId);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (showOrgFilter) {
      orgManagementService.listOrgs().then(setOrgs).catch(() => setOrgs([]));
    }
  }, [showOrgFilter]);

  const fetchDashboardData = async (orgId?: number) => {
    try {
      setLoading(true);
      // dashboard endpoint 一把拿 schoolCount/questionnaireCount/publishedCount/todayCompletedAnswers
      // recent + onboarding 仍单独拉 questionnaireService（带最新 5 条 + 全量 onboarding 计算）
      const [dashboardStats, questionnaireData] = await Promise.all([
        dashboardService.getDashboardStats(orgId),
        questionnaireService.getQuestionnaires({ page: 0, size: 5, sortBy: 'createdAt', sortDir: 'DESC' }),
      ]);

      const recentList = questionnaireData.content;
      const totalQ = dashboardStats.totalQuestionnaires;

      let allQ: Questionnaire[] = [];
      if (totalQ > 0) {
        const allData = await questionnaireService.getQuestionnaires({ page: 0, size: 1000 });
        allQ = allData.content;
      }

      setStats({
        schoolCount: dashboardStats.totalSchools,
        questionnaireCount: totalQ,
        todayCompletedAnswers: dashboardStats.todayCompletedAnswers,
        publishedCount: dashboardStats.totalPublished,
      });
      setAllQuestionnaires(allQ);
      setRecentQuestionnaires(recentList);
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleOrgFilterChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const val = e.target.value;
    const orgId = val === '' ? undefined : Number(val);
    setSelectedOrgId(orgId);
    fetchDashboardData(orgId);
  };

  const formatTime = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMin = Math.floor(diffMs / 60000);
    const diffHour = Math.floor(diffMin / 60);
    const diffDay = Math.floor(diffHour / 24);

    if (diffMin < 1) return '刚刚';
    if (diffMin < 60) return `${diffMin}分钟前`;
    if (diffHour < 24) return `${diffHour}小时前`;
    if (diffDay < 7) return `${diffDay}天前`;
    return date.toLocaleDateString('zh-CN');
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'DRAFT': return '草稿';
      case 'PUBLISHED': return '已发布';
      case 'ARCHIVED': return '已归档';
      default: return status;
    }
  };

  const getQuestionnaireIcon = (index: number) => {
    const icons = ['mood', 'self_improvement', 'family_history', 'psychology', 'diversity_3'];
    return icons[index % icons.length];
  };

  const getQuestionnaireIconColor = (index: number) => {
    const colors = [
      { bg: 'bg-primary/5', text: 'text-primary', hoverText: 'group-hover:text-primary' },
      { bg: 'bg-secondary/5', text: 'text-secondary', hoverText: 'group-hover:text-secondary' },
      { bg: 'bg-tertiary/5', text: 'text-tertiary', hoverText: 'group-hover:text-tertiary' },
      { bg: 'bg-primary/5', text: 'text-primary', hoverText: 'group-hover:text-primary' },
      { bg: 'bg-secondary/5', text: 'text-secondary', hoverText: 'group-hover:text-secondary' },
    ];
    return colors[index % colors.length];
  };

  const statCards = [
    {
      label: '注册学校',
      value: stats.schoolCount,
      icon: 'school',
      iconBg: 'bg-primary-container',
      iconColor: 'text-primary group-hover:text-white',
      hoverBg: 'hover:bg-serene',
      trendBg: 'bg-primary/10 group-hover:bg-white/20',
      trendColor: 'text-primary group-hover:text-white',
      trend: '+4%',
      trendIcon: 'trending_up',
      onClick: () => navigate('/schools'),
    },
    {
      label: '问卷总数',
      value: stats.questionnaireCount,
      icon: 'assignment',
      iconBg: 'bg-secondary-container',
      iconColor: 'text-secondary group-hover:text-white',
      hoverBg: 'hover:bg-secondary',
      trendBg: 'bg-secondary/10 group-hover:bg-white/20',
      trendColor: 'text-secondary group-hover:text-white',
      trend: '+12%',
      trendIcon: 'trending_up',
      onClick: () => navigate('/questionnaires'),
    },
    {
      label: '今日新增答卷',
      value: stats.todayCompletedAnswers,
      icon: 'today',
      iconBg: 'bg-tertiary-container',
      iconColor: 'text-tertiary group-hover:text-white',
      hoverBg: 'hover:bg-tertiary',
      trendBg: 'bg-slate-100 group-hover:bg-white/20',
      trendColor: 'text-slate-400 group-hover:text-white',
      trend: 'Today',
      trendIcon: null,
      onClick: () => navigate('/questionnaires'),
    },
    {
      label: '已发布',
      value: stats.publishedCount,
      icon: 'rocket_launch',
      iconBg: 'bg-surface-container-high',
      iconColor: 'text-slate-600 group-hover:text-white',
      hoverBg: 'hover:bg-slate-900',
      trendBg: 'bg-teal-50 group-hover:bg-white/20',
      trendColor: 'text-teal-600 group-hover:text-primary-fixed',
      trend: 'Live',
      trendIcon: 'verified',
      onClick: () => navigate('/questionnaires'),
    },
  ];

  const quickActions = [
    {
      title: '管理学校',
      desc: '添加新的学校机构，调整区域设置和班级信息',
      icon: 'domain',
      iconColor: 'text-primary',
      hoverBorder: 'hover:border-primary/10',
      onClick: () => navigate('/schools'),
    },
    {
      title: '管理问卷',
      desc: '创建、编辑和验证心理评估问卷模板',
      icon: 'fact_check',
      iconColor: 'text-secondary',
      hoverBorder: 'hover:border-secondary/10',
      onClick: () => navigate('/questionnaires'),
    },
    {
      title: '统计分析',
      desc: '生成深度报告和各区域趋势分析',
      icon: 'analytics',
      iconColor: 'text-tertiary',
      hoverBorder: 'hover:border-tertiary/10',
      onClick: () => navigate('/site-statistics'),
    },
    {
      title: '推送问卷',
      desc: '向选定的学校和班级分发已发布的问卷',
      icon: 'send',
      iconColor: 'text-slate-800',
      hoverBorder: 'hover:border-slate-800/10',
      onClick: () => navigate('/questionnaires'),
    },
  ];

  return (
    <div className="px-8 py-8">
      {/* Hero Welcome Section */}
      <section className="relative overflow-hidden rounded-[2.5rem] serene-gradient p-12 flex items-center justify-between shadow-lg">
        <div className="relative z-10 max-w-2xl">
          <div className="flex items-center gap-2 mb-6">
            <span className="px-3 py-1 rounded-full bg-white/20 text-white text-[10px] font-bold tracking-widest uppercase backdrop-blur-md font-label">
              系统概览
            </span>
            <span className="w-1.5 h-1.5 rounded-full bg-white/60"></span>
            <span className="text-white/80 text-[10px] font-semibold tracking-wide uppercase font-label">
              系统运行正常
            </span>
          </div>
          <h2 className="text-5xl font-extrabold text-white mb-4 tracking-tight leading-tight font-headline">
            欢迎回来, {user?.username}.
          </h2>
          <p className="text-white/90 text-xl font-light leading-relaxed italic mb-6 font-body">
            "Today is a good day to listen."
          </p>
          <div className="flex items-center gap-4 text-white/80 text-sm">
            <div className="flex items-center gap-2 px-4 py-2 bg-white/10 rounded-full backdrop-blur-sm">
              <span className="material-symbols-outlined text-sm">notifications_active</span>
              <span className="font-medium font-label">{stats.schoolCount} 所学校</span>
            </div>
            <div className="flex items-center gap-2 px-4 py-2 bg-white/10 rounded-full backdrop-blur-sm">
              <span className="material-symbols-outlined text-sm">pending_actions</span>
              <span className="font-medium font-label">{stats.publishedCount} 份已发布</span>
            </div>
          </div>
        </div>
        {/* Decorative Organic Shape (right side) */}
        <div className="hidden lg:block relative z-10 mr-8">
          <div className="relative w-72 h-72 flex items-center justify-center">
            <div className="absolute inset-0 bg-gradient-to-tr from-primary/30 to-secondary/30 rounded-full blur-[60px] animate-pulse"></div>
            <div className="absolute inset-4 bg-gradient-to-bl from-teal-300/40 to-blue-400/40 rounded-full blur-[40px] opacity-80"></div>
            <div className="absolute inset-16 bg-white/40 rounded-full blur-[20px]" style={{ boxShadow: '0 0 50px rgba(255,255,255,0.4)' }}></div>
            <div className="absolute w-full h-full animate-spin" style={{ animationDuration: '10s' }}>
              <div className="absolute top-0 left-1/2 -translate-x-1/2 w-4 h-4 bg-white/60 rounded-full blur-md"></div>
            </div>
            <svg className="absolute inset-0 w-full h-full opacity-30 mix-blend-overlay" viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg">
              <path d="M44.7,-76.4C58.1,-69.2,69.2,-58.1,76.4,-44.7C83.7,-31.3,87.1,-15.7,86.4,-0.4C85.7,14.8,80.9,29.6,73,43C65.1,56.4,54.1,68.4,40.7,75.3C27.3,82.2,11.5,84,1,82.2C-9.5,80.5,-18.9,75.2,-31.2,68.9C-43.5,62.6,-58.6,55.3,-68.8,44C-79,32.7,-84.3,17.4,-84.7,2.1C-85.1,-13.2,-80.6,-28.5,-71.3,-41.2C-62,-53.9,-47.9,-64.1,-33.5,-70.7C-19.1,-77.3,-4.4,-80.4,11.3,-82.3C27,-84.2,40.1,-84.9,44.7,-76.4Z" fill="white" transform="translate(100 100)" />
            </svg>
          </div>
        </div>
        {/* Abstract Background Elements */}
        <div className="absolute -top-20 -left-20 w-80 h-80 bg-white/10 rounded-full blur-[100px]"></div>
        <div className="absolute top-1/2 -right-20 w-96 h-96 bg-primary-fixed/20 rounded-full blur-[120px]"></div>
      </section>

      {showOrgFilter && (
        <section className="mt-8 flex items-center gap-3">
          <label htmlFor="dashboard-org-filter" className="text-sm font-medium text-gray-700">机构筛选</label>
          <select
            id="dashboard-org-filter"
            aria-label="机构筛选"
            value={selectedOrgId ?? ''}
            onChange={handleOrgFilterChange}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary/40"
          >
            <option value="">全部机构</option>
            {orgs.map((o) => (
              <option key={o.id} value={o.id}>{o.name}</option>
            ))}
          </select>
        </section>
      )}

      {/* Key Metrics Grid */}
      <section className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mt-8">
        {statCards.map((card) => (
          <div
            key={card.label}
            onClick={card.onClick}
            className={`bg-white/90 backdrop-blur-xl p-7 rounded-3xl shadow-xl group ${card.hoverBg} transition-all duration-500 cursor-pointer`}
          >
            <div className="flex justify-between items-start mb-5">
              <div className={`p-3.5 rounded-2xl ${card.iconBg} group-hover:bg-white/20 transition-colors`}>
                <span className={`material-symbols-outlined ${card.iconColor}`}>{card.icon}</span>
              </div>
              {card.trend && (
                <span className={`px-2 py-1 rounded-lg text-[10px] font-bold ${card.trendColor} ${card.trendBg} flex items-center gap-1 transition-colors font-label`}>
                  {card.trendIcon && <span className="material-symbols-outlined text-[12px]">{card.trendIcon}</span>}
                  {card.trend}
                </span>
              )}
            </div>
            <p className="text-slate-500 group-hover:text-white/70 text-sm font-semibold transition-colors font-body">{card.label}</p>
            <h3 className="text-4xl font-black text-on-surface group-hover:text-white transition-colors mt-1 font-headline">
              {loading ? <span className="text-surface-dim">--</span> : card.value.toLocaleString()}
            </h3>
          </div>
        ))}
      </section>

      {/* Onboarding Guide */}
      {onboarding.isVisible && (
        <div className="pt-8">
          <OnboardingGuide
            completedSteps={onboarding.completedSteps}
            onSkip={onboarding.skip}
            onDismiss={onboarding.dismiss}
          />
        </div>
      )}

      {/* Main Interactive Bento Grid */}
      <div className="grid grid-cols-12 gap-8 pt-12">
        {/* Quick Actions - Left 8 cols */}
        <div className="col-span-12 lg:col-span-8 space-y-6">
          <div className="flex items-center justify-between">
            <h4 className="text-xl font-extrabold tracking-tight font-headline">快速操作</h4>
            <button
              onClick={() => navigate('/questionnaires')}
              className="text-primary text-sm font-semibold flex items-center gap-1 hover:underline font-label"
            >
              查看全部 <span className="material-symbols-outlined text-sm">arrow_forward</span>
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {quickActions.map((action) => (
              <div
                key={action.title}
                onClick={action.onClick}
                className={`group bg-surface-container-low hover:bg-surface-container-lowest p-8 rounded-[2rem] flex flex-col justify-between h-56 transition-all duration-300 cursor-pointer border-2 border-transparent ${action.hoverBorder}`}
              >
                <div className="w-12 h-12 bg-white rounded-2xl flex items-center justify-center shadow-sm group-hover:scale-110 transition-transform">
                  <span className={`material-symbols-outlined ${action.iconColor}`}>{action.icon}</span>
                </div>
                <div>
                  <h5 className="text-lg font-bold mb-1 font-headline">{action.title}</h5>
                  <p className="text-slate-500 text-sm leading-relaxed font-body">{action.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Secondary Column - Right 4 cols (融合卡片) */}
        <div className="col-span-12 lg:col-span-4 lg:h-full">
          <div className="relative h-full flex flex-col rounded-[2rem] overflow-hidden bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white">
            {/* 装饰光晕 */}
            <div className="absolute -top-20 -right-16 w-56 h-56 bg-primary/25 rounded-full blur-3xl pointer-events-none"></div>
            <div className="absolute -bottom-16 -left-16 w-48 h-48 bg-primary/15 rounded-full blur-3xl pointer-events-none"></div>

            {/* 区段 1：最近问卷 */}
            <div className="relative px-7 pt-7 pb-5 flex flex-col min-h-0 flex-1">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <span className="inline-flex items-center justify-center w-7 h-7 rounded-lg bg-primary/20 text-primary-300">
                    <span className="material-symbols-outlined" style={{ fontSize: '16px' }}>history</span>
                  </span>
                  <h4 className="text-base font-bold tracking-tight font-headline">最近问卷</h4>
                </div>
                <button
                  onClick={() => navigate('/questionnaires')}
                  className="text-[11px] font-medium text-slate-300 hover:text-white transition-colors flex items-center gap-0.5 font-label"
                >
                  全部 <span className="material-symbols-outlined" style={{ fontSize: '14px' }}>chevron_right</span>
                </button>
              </div>
              <div className="flex-1 min-h-0 overflow-y-auto">
                {loading ? (
                  <div className="text-center py-6">
                    <div className="inline-block animate-spin rounded-full h-5 w-5 border-b-2 border-white/60"></div>
                  </div>
                ) : recentQuestionnaires.length === 0 ? (
                  <p className="text-xs text-slate-400 text-center py-6 font-body">暂无问卷</p>
                ) : (
                  <div className="space-y-2">
                    {recentQuestionnaires.slice(0, 3).map((q) => (
                      <div
                        key={q.id}
                        onClick={() => navigate(`/questionnaires/${q.id}`)}
                        className="group flex items-center gap-3 px-3 py-2.5 rounded-xl bg-white/5 hover:bg-white/10 border border-white/5 transition-colors cursor-pointer"
                      >
                        <span className={`shrink-0 w-2 h-2 rounded-full ${
                          q.status === 'PUBLISHED' ? 'bg-emerald-400' :
                          q.status === 'DRAFT' ? 'bg-amber-400' :
                          'bg-rose-400'
                        } shadow-[0_0_8px_currentColor]`}></span>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate text-white/95 font-body">{q.title}</p>
                          <p className="text-[10.5px] text-slate-400 font-label uppercase tracking-wider mt-0.5">
                            {getStatusLabel(q.status)} · {formatTime(q.createdAt)}
                          </p>
                        </div>
                        <span className="material-symbols-outlined text-slate-500 group-hover:text-white/70 transition-colors" style={{ fontSize: '18px' }}>arrow_forward</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* 分隔 */}
            <div className="relative px-7">
              <div className="border-t border-white/10"></div>
            </div>

            {/* 区段 2：实时动态 */}
            <div className="relative px-7 pt-5 pb-7 flex flex-col min-h-0 flex-1">
              <div className="flex items-center gap-2 mb-4">
                <span className="relative inline-flex items-center justify-center w-7 h-7">
                  <span className="absolute inset-0 rounded-full bg-primary/30 animate-ping"></span>
                  <span className="relative w-2.5 h-2.5 rounded-full bg-primary-300 shadow-[0_0_8px_currentColor]"></span>
                </span>
                <h4 className="text-base font-bold tracking-tight font-headline">实时动态</h4>
              </div>
              <div className="flex-1 min-h-0 overflow-y-auto pl-2">
                {recentQuestionnaires.length === 0 ? (
                  <p className="text-xs text-slate-400 text-center py-6 font-body">暂无动态</p>
                ) : (
                  <div className="relative">
                    {/* 时间线连接 */}
                    <div className="absolute left-[5px] top-2 bottom-2 w-px bg-gradient-to-b from-primary/50 via-primary/20 to-transparent"></div>
                    <div className="space-y-3.5">
                      {recentQuestionnaires.slice(0, 3).map((q) => (
                        <div key={`activity-${q.id}`} className="relative flex gap-3 items-start">
                          <span className={`relative z-10 mt-1.5 shrink-0 w-3 h-3 rounded-full border-2 border-slate-800 shadow ${
                            q.status === 'PUBLISHED' ? 'bg-primary-300' :
                            q.status === 'DRAFT' ? 'bg-amber-400' :
                            'bg-rose-400'
                          }`}></span>
                          <div className="flex-1 min-w-0 pb-1">
                            <p className="text-sm font-semibold font-body text-white/95 truncate">{q.title}</p>
                            <p className="text-[11.5px] text-slate-300 font-body mt-0.5">
                              {q.status === 'PUBLISHED' ? '问卷已发布并分发中' : q.status === 'DRAFT' ? '问卷草稿编辑中' : '问卷已禁用'}
                            </p>
                            <p className="text-[10.5px] text-slate-400 mt-1 font-label tracking-wide">{formatTime(q.createdAt)}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Promotional/Tip Glass Card */}
      <section className="bg-gradient-to-r from-secondary-container to-tertiary-container rounded-[2rem] p-1 shadow-xl mt-8">
        <div className="bg-white/90 backdrop-blur-md rounded-[1.9rem] p-8 flex flex-col md:flex-row items-center gap-8">
          <div className="w-16 h-16 rounded-full bg-tertiary flex items-center justify-center shrink-0">
            <span className="material-symbols-outlined text-white text-3xl" style={{ fontVariationSettings: "'FILL' 1" }}>lightbulb</span>
          </div>
          <div className="flex-1">
            <h4 className="text-xl font-bold text-on-surface mb-2 font-headline">提示：自动化情绪分析</h4>
            <p className="text-slate-600 leading-relaxed max-w-2xl font-body">使用问卷管理功能，您可以创建、编辑和分发心理测评问卷。开放式问题现已支持实时情绪分析，帮助发现选择题可能遗漏的隐性心理信号。</p>
          </div>
          <button
            onClick={() => navigate('/questionnaires')}
            className="bg-primary text-white px-8 py-3 rounded-xl font-bold shadow-lg shadow-primary/20 hover:scale-105 transition-transform shrink-0 font-label"
          >
            了解更多
          </button>
        </div>
      </section>
    </div>
  );
};

export default DashboardPage;
